"use client";

import { useEffect, useRef } from "react";
import { useStudio } from "@/contexts/studio-context";

/**
 * The player scrolled the roll and let go: `onScrub` fires with the tick the
 * view came to rest on.
 *
 * ## Why the roll is a control here and was not
 *
 * Every step of the parcours walks a passage from its first attack, and the
 * roll follows the step. The player could already move the roll — it is the
 * one gesture the piano-roll has always had — but nothing read it, so
 * scrolling to the bar that will not come out and playing it again meant
 * starting the whole passage over. On a unit of four bars that is three bars
 * of tax on every repetition of the fourth, and the fourth is the one being
 * worked.
 *
 * So the gesture the player already makes becomes the answer to the question
 * they were already asking: **the passage resumes where you are looking**.
 *
 * ## Only the player's own gesture
 *
 * `Studio.onViewScrub` is silent while the application scrolls the roll,
 * which it does on every attack of a run. Without that distinction the run
 * would retarget itself on its own auto-scroll and never advance.
 *
 * `enabled` is the other half: a pass in time owns the transport, and a
 * gesture during one pauses it. The steps switch this off while they run.
 */
export function useRollScrub(
  enabled: boolean,
  onScrub: (tick: number) => void,
): void {
  const { studio } = useStudio();

  // Latched so the subscription does not re-attach on every render of a
  // panel that re-renders on every attack.
  const callbackRef = useRef(onScrub);

  useEffect(() => {
    callbackRef.current = onScrub;
  }, [onScrub]);

  useEffect(() => {
    if (studio === null || !enabled) {
      return;
    }

    return studio.onViewScrub((tick) => {
      callbackRef.current(tick);
    });
  }, [studio, enabled]);
}
